import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Play, Pause, RotateCcw } from 'lucide-react';

const ConditioningSession = ({ move, level, onClose }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [completedExercises, setCompletedExercises] = useState([]);

  // Placeholder exercises - to be filled later
  const exercises = [
    {
      name: "Core Strength",
      description: "Build core strength for better control",
      duration: 300, // 5 minutes
      sets: 3
    },
    {
      name: "Upper Body Conditioning",
      description: "Strengthen arms and shoulders",
      duration: 240, // 4 minutes
      sets: 2
    },
    {
      name: "Lower Body Power",
      description: "Develop leg strength and power",
      duration: 360, // 6 minutes
      sets: 3
    }
  ];

  const startSession = () => {
    setIsActive(true);
    setCurrentExercise(0);
    setTimeLeft(exercises[0].duration);
  };

  const pauseSession = () => {
    setIsActive(false);
  };

  const resetSession = () => {
    setIsActive(false);
    setCurrentExercise(0);
    setTimeLeft(exercises[0].duration);
    setCompletedExercises([]);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const nextExercise = () => {
    if (currentExercise < exercises.length - 1) {
      setCurrentExercise(currentExercise + 1);
      setTimeLeft(exercises[currentExercise + 1].duration);
    } else {
      // Session completed
      setIsActive(false);
    }
  };

  // Timer effect
  React.useEffect(() => {
    let interval = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && isActive) {
      nextExercise();
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft, currentExercise]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Move
        </Button>
        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
          Conditioning Session
        </Badge>
      </div>

      <div className="text-center">
        <h3 className="text-xl font-bold mb-2">Conditioning for {move}</h3>
        <p className="text-sm text-gray-600 mb-4">
          Build strength and endurance for better performance
        </p>
      </div>

      {!isActive ? (
        <div className="space-y-4">
          <div className="text-center">
            <h4 className="text-lg font-semibold mb-2">Session Overview</h4>
            <p className="text-sm text-gray-600 mb-4">
              {exercises.length} exercises • ~{Math.round(exercises.reduce((acc, ex) => acc + ex.duration, 0) / 60)} minutes
            </p>
          </div>

          <div className="space-y-2">
            {exercises.map((exercise, index) => (
              <Card key={index} className="border border-gray-200">
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <h5 className="font-medium">{exercise.name}</h5>
                      <p className="text-sm text-gray-600">{exercise.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatTime(exercise.duration)}</p>
                      <p className="text-xs text-gray-500">{exercise.sets} sets</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Button onClick={startSession} className="w-full bg-blue-600 text-white">
            <Play className="w-4 h-4 mr-2" />
            Start Conditioning Session
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <Card className="border border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-center text-blue-800">
                Exercise {currentExercise + 1} of {exercises.length}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <h4 className="text-xl font-bold mb-2">{exercises[currentExercise].name}</h4>
              <p className="text-sm text-gray-600 mb-4">{exercises[currentExercise].description}</p>
              <div className="text-3xl font-bold text-blue-600 mb-4">
                {formatTime(timeLeft)}
              </div>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={pauseSession}
                  variant="outline"
                  size="sm"
                  className="border-blue-300 text-blue-700"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </Button>
                <Button
                  onClick={resetSession}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 text-gray-700"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ConditioningSession; 